import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const CART_TRAY_OPEN_EVENT = 'cart-tray:open';
const CART_TRAY_CLOSE_EVENT = 'cart-tray:close';

/**
 * VoidScreen's modal cart surface. The element keeps Horizon's public
 * `#cart-drawer` action and Section Rendering contracts, but never squeezes or
 * persists the page. Its dialog is positioned from one header-shell read.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 *
 * @extends {Component<Refs>}
 */
class CartDrawerComponent extends Component {
  requiredRefs = ['panel'];

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;
  /** @type {HTMLElement | null} */
  #infoTrigger = null;
  /** @type {Promise<void> | null} */
  #openingPromise = null;
  /** @type {Promise<void> | null} */
  #closingPromise = null;
  /** @type {number} */
  #openGeneration = 0;
  /** @type {number | null} */
  #resizeFrame = null;

  get isOpen() {
    return this.hasAttribute('open') && this.refs.panel.open;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.refs.panel.addEventListener('cancel', this.#onPanelCancel);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.addEventListener('theme-drawer:open', this.#onCompetingDrawerOpen);
    window.addEventListener('resize', this.#onResize, { passive: true });

    const search = document.getElementById('search-modal');
    search?.addEventListener('dialog:open', this.#onSearchOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.refs.panel.removeEventListener('cancel', this.#onPanelCancel);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.removeEventListener('theme-drawer:open', this.#onCompetingDrawerOpen);
    window.removeEventListener('resize', this.#onResize);
    document.getElementById('search-modal')?.removeEventListener('dialog:open', this.#onSearchOpen);
    if (this.#resizeFrame !== null) cancelAnimationFrame(this.#resizeFrame);
    unlockScroll(this.refs.panel);
    document.getElementById('header-component')?.removeAttribute('data-cart-tray-open');
  }

  /** Open the tray without page reflow or session persistence. */
  open() {
    if (this.isOpen) return Promise.resolve();
    if (this.#openingPromise) return this.#openingPromise;
    if (this.#closingPromise) {
      const generation = ++this.#openGeneration;
      return this.#closingPromise.then(() => {
        if (generation !== this.#openGeneration) return;
        return this.open();
      });
    }

    const generation = ++this.#openGeneration;
    this.#openingPromise = this.#open(generation).finally(() => {
      this.#openingPromise = null;
    });
    return this.#openingPromise;
  }

  /** @param {number} generation */
  async #open(generation) {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const closingSurfaces = this.#closeCompetingSurfaces();
    if (closingSurfaces) await closingSurfaces;
    if (generation !== this.#openGeneration) return;

    const panel = this.refs.panel;
    this.#previouslyFocused = opener;
    lockScroll(panel);
    const geometry = this.#readHeaderGeometry();
    this.#writeGeometry(geometry);

    panel.classList.add('cart-tray__dialog--opening');
    try {
      panel.showModal();
    } catch (error) {
      panel.classList.remove('cart-tray__dialog--opening');
      unlockScroll(panel);
      throw error;
    }
    this.setAttribute('open', '');
    document.getElementById('header-component')?.setAttribute('data-cart-tray-open', '');
    this.dispatchEvent(new CartTrayOpenEvent());

    requestAnimationFrame(() => this.querySelector('[data-cart-tray-close]')?.focus());
    onAnimationEnd(panel, () => panel.classList.remove('cart-tray__dialog--opening'), { subtree: false });
    this.#enableRecommendations();
    this.#wireInstallmentsLink();
  }

  /**
   * Close the tray.
   * @param {{ restoreFocus?: boolean, immediate?: boolean }} [options]
   */
  close(options = {}) {
    if (this.#closingPromise) {
      this.#openGeneration += 1;
      return this.#closingPromise;
    }
    if (this.#openingPromise) {
      this.#openGeneration += 1;
      return this.#openingPromise.then(() => this.close(options));
    }
    if (!this.isOpen) return Promise.resolve();

    this.#closingPromise = this.#close(options).finally(() => {
      this.#closingPromise = null;
    });
    return this.#closingPromise;
  }

  async #close({ restoreFocus = true, immediate = false } = {}) {
    const panel = this.refs.panel;
    const infoDialog = this.#infoDialog;
    if (infoDialog?.open) infoDialog.close();

    panel.classList.remove('cart-tray__dialog--opening');
    if (!immediate) {
      panel.classList.add('cart-tray__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('cart-tray__dialog--closing');
    }

    if (panel.open) panel.close();
    unlockScroll(panel);
    this.removeAttribute('open');
    document.getElementById('header-component')?.removeAttribute('data-cart-tray-open');
    this.dispatchEvent(new CartTrayCloseEvent());

    if (restoreFocus) {
      const target = this.#previouslyFocused;
      const targetIsHiddenSurface = target?.closest('dialog:not([open]), theme-drawer:not([open])');
      const fallback = /** @type {HTMLElement | null} */ (document.querySelector('[aria-controls="cart-drawer"]'));
      const focusTarget = target && document.contains(target) && !targetIsHiddenSurface ? target : fallback;
      this.#restoreFocus(focusTarget);
    }
    this.#previouslyFocused = null;
  }

  /**
   * Restore the logical focus position without showing a fresh focus ring for
   * the close action itself. The marker is removed as soon as focus moves on,
   * so deliberate keyboard navigation keeps its normal visible indicator.
   * @param {HTMLElement | null} target
   */
  #restoreFocus(target) {
    if (!target) return;
    target.setAttribute('data-vs-restored-focus', '');
    target.addEventListener('blur', () => target.removeAttribute('data-vs-restored-focus'), { once: true });
    target.focus({ preventScroll: true });
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /** @returns {HTMLDialogElement | null} */
  get #infoDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-cart-tray-info-dialog]'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-cart-tray-close]')) {
      this.close();
      return;
    }

    if (target.closest('[data-cart-tray-info-open]')) {
      const dialog = this.#infoDialog;
      if (!dialog || dialog.open) return;
      this.#infoTrigger = target.closest('[data-cart-tray-info-open]');
      dialog.showModal();
      dialog.addEventListener('cancel', this.#onInfoCancel, { once: true });
      requestAnimationFrame(() => dialog.querySelector('[data-cart-tray-info-close]')?.focus());
      return;
    }

    if (target.closest('[data-cart-tray-info-close]')) {
      this.#closeInfoDialog();
      return;
    }

    const infoDialog = this.#infoDialog;
    if (event.target === infoDialog && isClickedOutside(event, infoDialog)) {
      this.#closeInfoDialog();
      return;
    }

    if (event.target === this.refs.panel && isClickedOutside(event, this.refs.panel) && !this.#infoDialog?.open) {
      this.close();
    }
  };

  /** @param {Event} event */
  #onPanelCancel = (event) => {
    event.preventDefault();
    if (this.#infoDialog?.open) {
      this.#closeInfoDialog();
      return;
    }
    this.close();
  };

  /** @param {Event} event */
  #onInfoCancel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#closeInfoDialog();
  };

  #closeInfoDialog() {
    const dialog = this.#infoDialog;
    if (!dialog?.open) return;
    dialog.close();
    if (this.#infoTrigger && document.contains(this.#infoTrigger)) this.#infoTrigger.focus();
    this.#infoTrigger = null;
  }

  #onResize = () => {
    if (!this.isOpen || this.#resizeFrame !== null) return;
    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = null;
      this.#writeGeometry(this.#readHeaderGeometry());
    });
  };

  #readHeaderGeometry() {
    const shell = document.querySelector('#header-component .header__row--top .header__columns');
    if (shell instanceof HTMLElement) {
      const rect = shell.getBoundingClientRect();
      return {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        headerHeight: Math.round(rect.height),
      };
    }
    return { top: 12, left: 16, width: Math.max(0, window.innerWidth - 32), headerHeight: 56 };
  }

  /** @param {{top: number, left: number, width: number, headerHeight: number}} geometry */
  #writeGeometry({ top, left, width, headerHeight }) {
    this.style.setProperty('--cart-tray-top', `${Math.max(0, top)}px`);
    this.style.setProperty('--cart-tray-left', `${Math.max(0, left)}px`);
    this.style.setProperty('--cart-tray-width', `${Math.max(0, width)}px`);
    this.style.setProperty('--cart-tray-header-height', `${Math.max(0, headerHeight)}px`);
  }

  #closeCompetingSurfaces() {
    const tasks = [];
    const headerMenu = /** @type {HTMLElement & { close?: () => void } | null} */ (document.querySelector('header-menu'));
    headerMenu?.close?.();

    const headerDrawer = /** @type {HTMLElement & { isOpen?: boolean, close?: (options?: object) => Promise<void> } | null} */ (
      document.querySelector('header-drawer')
    );
    // A hidden menu has no transition to wait for. On phones hand off in the
    // same turn, without restoring focus to a control behind the cart.
    if (headerDrawer?.isOpen) {
      const immediate = window.innerWidth <= 839;
      const closing = headerDrawer.close?.({ immediate, restoreFocus: false });
      if (closing && !immediate) tasks.push(closing);
    }

    const search = /** @type {HTMLElement & { closeDialog?: () => Promise<void> } | null} */ (
      document.getElementById('search-modal')
    );
    if (search?.querySelector('dialog[open]') && search.closeDialog) tasks.push(search.closeDialog());

    for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
      const component = /** @type {HTMLElement & { close?: () => Promise<void> }} */ (drawer);
      if (component.close) tasks.push(component.close());
    }

    return tasks.length ? Promise.allSettled(tasks) : null;
  }

  #onCompetingDrawerOpen = () => {
    if (this.isOpen || this.#openingPromise) this.close({ restoreFocus: false, immediate: true });
  };

  #onSearchOpen = () => {
    if (this.isOpen || this.#openingPromise) this.close({ restoreFocus: false, immediate: true });
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.isOpen;
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) this.open();

    event.promise
      ?.then(({ detail }) => {
        if (!shouldAutoOpen || detail?.didError) return;
        const openTray = () => {
          if (!this.isOpen) this.open();
        };
        if (sourceModal?.open) sourceModal.addEventListener('close', openTray, { once: true });
        else openTray();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-tray] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #wireInstallmentsLink() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.close({ restoreFocus: false, immediate: true }), { once: true });
    });
  }

  async #enableRecommendations() {
    if (this.#isCartEmpty() || !this.querySelector('cart-tray-recommendations')) return;
    try {
      await import('@theme/cart-tray-recommendations');
      const recommendations = /** @type {HTMLElement & { load?: (options?: {retryFailed?: boolean}) => Promise<void> } | null} */ (
        this.querySelector('cart-tray-recommendations')
      );
      recommendations?.load?.({ retryFailed: true });
    } catch (error) {
      if (error instanceof Error) console.warn('[cart-tray] Recommendations module unavailable:', error.message);
    }
  }
}

export class CartTrayOpenEvent extends CustomEvent {
  static eventName = CART_TRAY_OPEN_EVENT;
  constructor() {
    super(CartTrayOpenEvent.eventName, { bubbles: true });
  }
}

export class CartTrayCloseEvent extends CustomEvent {
  static eventName = CART_TRAY_CLOSE_EVENT;
  constructor() {
    super(CartTrayCloseEvent.eventName, { bubbles: true });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
